import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell, 
  faExclamationTriangle, 
  faExclamationCircle,
  faInfoCircle,
  faCheckCircle,
  faCog,
  faPlus,
  faFilter,
  faSync,
  faCloud,
  faChartLine,
  faUsers,
  faClock,
  faEye,
  faVolumeXmark,
  faCheck,
  faTrash,
  faEdit,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { useAlertsData } from './hooks/useAlertsData';
import { Alert, AlertFilters } from './types';

interface ConnectedAccount {
  id: string;
  name: string;
  type: 'aws' | 'gcp' | 'github' | 'jenkins';
  status: 'active' | 'inactive' | 'error';
  connectionMethod?: 'iam' | 'cloudformation' | 'oauth' | 'token';
  accountId?: string;
  region?: string;
  lastSync?: string;
  config?: any;
}

interface AlertsTabProps {
  connectedAccounts?: ConnectedAccount[];
}

export const AlertsTab: React.FC<AlertsTabProps> = ({ connectedAccounts = [] }) => {
  console.log('=== DEBUG: AlertsTab component rendering ===');
  console.log('=== DEBUG: Received connectedAccounts ===', connectedAccounts);
  
  // Create initial filters with connected account IDs
  const initialFilters: AlertFilters = {
    accountId: connectedAccounts.length > 0 ? connectedAccounts
      .filter(acc => acc.accountId)
      .map(acc => acc.accountId!) : undefined
  };
  
  console.log('=== DEBUG: Initial filters for alerts ===', initialFilters);
  
  const {
    alerts,
    overview,
    notificationChannels,
    loading,
    error,
    filters,
    setFilters,
    refetch,
    lastUpdated,
    acknowledgeAlert,
    suppressAlert,
    deleteAlert
  } = useAlertsData({
    autoRefresh: true,
    refreshInterval: 30000,
    enableLiveUpdates: true,
    initialFilters: initialFilters
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return faExclamationTriangle;
      case 'high': return faExclamationTriangle;
      case 'medium': return faExclamationCircle;
      case 'low': return faInfoCircle;
      default: return faInfoCircle;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600';
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-800';
      case 'acknowledged': return 'bg-yellow-100 text-yellow-800';
      case 'suppressed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStateBadgeColor = (state: string) => {
    switch (state) {
      case 'alarm': return 'bg-red-100 text-red-800';
      case 'ok': return 'bg-green-100 text-green-800';
      case 'insufficient_data': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'aws': return faAws;
      case 'gcp': return faGoogle;
      default: return faCloud;
    }
  };

  const getProviderColor = (provider: string) => {
    switch (provider) {
      case 'aws': return 'text-orange-500';
      case 'gcp': return 'text-blue-500';
      default: return 'text-gray-500';
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    try {
      await acknowledgeAlert(alertId);
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const handleSuppress = async (alertId: string) => {
    try {
      await suppressAlert(alertId, 3600); // Suppress for 1 hour
    } catch (error) {
      console.error('Failed to suppress alert:', error);
    }
  };

  const handleDelete = async (alertId: string) => {
    if (window.confirm('Are you sure you want to delete this alert?')) {
      try {
        await deleteAlert(alertId);
      } catch (error) {
        console.error('Failed to delete alert:', error);
      }
    }
  };

  if (loading && !alerts.length) {
    return (
      <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
        <div className="animate-pulse space-y-3">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
      {/* Alert Management Header */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
            <div>
              <div className="text-base font-bold text-gray-900">Alert Management</div>
              <div className="text-[10px] text-gray-600">
                {overview ?
                  `${overview.activeAlarms} active alerts • ${notificationChannels.filter(c => c.isActive).length} channels enabled` :
                  'Loading alert data...'
                }
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded text-xs font-medium hover:bg-gray-50"
            >
              <FontAwesomeIcon icon={faFilter} className="mr-1" />Filter
            </button>
            <button
              onClick={refetch}
              disabled={loading}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faSync} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] text-gray-500 mb-1">Total Alerts</div>
                <div className="text-sm font-semibold text-gray-900">
                  {overview.totalAlarms}
                </div>
              </div>
              <FontAwesomeIcon icon={faBell} className="text-gray-400 text-lg" />
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] text-red-500 mb-1">Active</div>
                <div className="text-sm font-semibold text-red-600">
                  {overview.activeAlarms}
                </div>
              </div>
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-400 text-lg" />
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] text-yellow-500 mb-1">Critical</div>
                <div className="text-sm font-semibold text-yellow-600">
                  {overview.criticalAlarms}
                </div>
              </div>
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-400 text-lg" />
            </div>
          </div>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] text-gray-500 mb-1">Suppressed</div>
                <div className="text-sm font-semibold text-gray-600">
                  {overview.suppressedAlarms}
                </div>
              </div>
              <FontAwesomeIcon icon={faVolumeXmark} className="text-gray-400 text-lg" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1">Provider</label>
              <select
                value={filters.service?.[0] || ''}
                onChange={(e) => setFilters({ ...filters, service: e.target.value ? [e.target.value] : undefined })}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
              >
                <option value="">All Providers</option>
                <option value="aws">AWS</option>
                <option value="gcp">GCP</option>
              </select>
            </div>
            
            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1">Severity</label>
              <select
                value={filters.severity?.[0] || ''}
                onChange={(e) => setFilters({ ...filters, severity: e.target.value ? [e.target.value] : undefined })}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
              >
                <option value="">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1">State</label>
              <select
                value={filters.state?.[0] || ''}
                onChange={(e) => setFilters({ ...filters, state: e.target.value ? [e.target.value] : undefined })}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
              >
                <option value="">All States</option>
                <option value="alarm">Alarm</option>
                <option value="ok">OK</option>
                <option value="insufficient_data">Insufficient Data</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status?.[0] || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value ? [e.target.value] : undefined })}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="suppressed">Suppressed</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search alerts..."
                value={filters.search || ''}
                onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
              />
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-2" />
            <span className="text-red-800 font-medium">Error: {error.message}</span>
          </div>
        </div>
      )}

      {/* Active Alerts */}
      <div className="bg-white border border-gray-300 rounded">
        <div className="p-3 border-b border-gray-300">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-2" />
              Active Alerts ({alerts.filter(a => a.status === 'active').length})
            </h3>
            <div className="text-[9px] text-gray-500">
              {lastUpdated && `Last updated: ${lastUpdated.toLocaleTimeString()}`}
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
          {alerts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-500 mb-2" />
              <p className="text-sm font-medium text-green-600 mb-1">No active alerts</p>
              <p className="text-xs">All systems are running smoothly</p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div key={alert.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mt-1">
                      <FontAwesomeIcon 
                        icon={getSeverityIcon(alert.severity)} 
                        className={getSeverityColor(alert.severity)}
                      />
                      <FontAwesomeIcon 
                        icon={getProviderIcon(alert.provider)} 
                        className={`text-xs ${getProviderColor(alert.provider)}`}
                        title={alert.provider.toUpperCase()}
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-gray-900 truncate">{alert.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStateBadgeColor(alert.state)}`}>
                          {alert.state.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(alert.status)}`}>
                          {alert.status.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          alert.severity === 'high' ? 'bg-red-50 text-red-600' :
                          alert.severity === 'medium' ? 'bg-yellow-50 text-yellow-600' :
                          'bg-blue-50 text-blue-600'
                        }`}>
                          {alert.severity.toUpperCase()}
                        </span>
                      </div>
                      
                      {alert.description && (
                        <p className="text-xs text-gray-600 mb-2">{alert.description}</p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[10px] text-gray-500">
                        <div>
                          <span className="font-medium">Account:</span> {alert.accountName}
                        </div>
                        <div>
                          <span className="font-medium">Region:</span> {alert.region}
                        </div>
                        <div>
                          <span className="font-medium">Service:</span> {alert.service}
                        </div>
                      </div>
                      
                      <div className="text-[10px] text-gray-500 mt-1">
                        <span className="font-medium">Metric:</span> {alert.metric.namespace}/{alert.metric.name}
                        {alert.threshold && (
                          <span className="ml-2">
                            <span className="font-medium">Threshold:</span> {alert.threshold.operator} {alert.threshold.value}
                            {alert.threshold.unit && ` ${alert.threshold.unit}`}
                          </span>
                        )}
                      </div>

                      {alert.lastTriggered && (
                        <div className="text-[9px] text-gray-500 mt-1">
                          <FontAwesomeIcon icon={faClock} className="mr-1" />
                          Triggered: {new Date(alert.lastTriggered).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 ml-4 flex-shrink-0">
                    <button
                      onClick={() => setSelectedAlert(alert)}
                      className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors"
                      title="View Details"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    
                    {alert.status === 'active' && (
                      <>
                        <button
                          onClick={() => handleAcknowledge(alert.id)}
                          className="px-2 py-1 bg-blue-100 text-blue-600 rounded text-xs hover:bg-blue-200 transition-colors"
                          title="Acknowledge"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                        
                        <button
                          onClick={() => handleSuppress(alert.id)}
                          className="px-2 py-1 bg-yellow-100 text-yellow-600 rounded text-xs hover:bg-yellow-200 transition-colors"
                          title="Suppress"
                        >
                          <FontAwesomeIcon icon={faVolumeXmark} />
                        </button>
                      </>
                    )}
                    
                    <button
                      onClick={() => handleDelete(alert.id)}
                      className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200 transition-colors"
                      title="Delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-3 border-b border-gray-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon 
                    icon={getSeverityIcon(selectedAlert.severity)} 
                    className={`text-lg ${getSeverityColor(selectedAlert.severity)}`}
                  />
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">{selectedAlert.name}</h3>
                    <p className="text-xs text-gray-500">{selectedAlert.accountName} • {selectedAlert.region}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedAlert(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-lg" />
                </button>
              </div>
            </div>

            <div className="p-3 space-y-3">
              {/* Alert Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <h4 className="text-xs font-bold text-gray-900 mb-2">Alert Information</h4>
                  <div className="space-y-2 text-[10px]">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Provider:</span>
                      <span className="font-medium">{selectedAlert.provider.toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Severity:</span>
                      <span className="font-medium">{selectedAlert.severity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">State:</span>
                      <span className="font-medium">{selectedAlert.state}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status:</span>
                      <span className="font-medium">{selectedAlert.status}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-bold text-gray-900 mb-2">Metric Configuration</h4>
                  <div className="space-y-2 text-[10px]">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Namespace:</span>
                      <span className="font-medium">{selectedAlert.metric.namespace}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Metric:</span>
                      <span className="font-medium">{selectedAlert.metric.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Statistic:</span>
                      <span className="font-medium">{selectedAlert.statistic}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Period:</span>
                      <span className="font-medium">{selectedAlert.period}s</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Description */}
              {selectedAlert.description && (
                <div>
                  <h4 className="text-xs font-bold text-gray-900 mb-2">Description</h4>
                  <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">{selectedAlert.description}</p>
                </div>
              )}
              
              {/* Threshold Information */}
              <div>
                <h4 className="text-xs font-bold text-gray-900 mb-2">Threshold Configuration</h4>
                <div className="bg-gray-50 p-2 rounded text-[10px]">
                  <p>
                    <span className="font-medium">Condition:</span> {selectedAlert.threshold.operator} {selectedAlert.threshold.value}
                    {selectedAlert.threshold.unit && ` ${selectedAlert.threshold.unit}`}
                  </p>
                  <p>
                    <span className="font-medium">Evaluation Periods:</span> {selectedAlert.evaluationPeriods}
                  </p>
                  <p>
                    <span className="font-medium">Datapoints to Alarm:</span> {selectedAlert.datapointsToAlarm}
                  </p>
                </div>
              </div>
              
              {/* Tags */}
              {Object.keys(selectedAlert.tags).length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-900 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedAlert.tags).map(([key, value]) => (
                      <span key={key} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {key}: {value}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsTab;